<template>
  <AppModal :is-open="isOpen" :title="title" :subtitle="instructionText" @close="emit('close')">
    <template v-if="game" #leading>
      <GameIndex :symbol="game.symbol" />
    </template>
    <UnitPlayerList
      :players="players"
      :allocation="allocation"
      :max="maxUnits"
      :unit-name="unitName"
      :points-for="pointsFor"
      :is-open="isOpen"
    />
    <template #footer>
      <p class="status" :class="{ 'is-done': allocation.isComplete.value }" aria-live="polite">
        {{ statusText }}
      </p>
      <button
        type="button"
        class="btn btn-ink btn-block"
        :disabled="!allocation.isComplete.value"
        @click="handleConfirm"
      >
        Save round
      </button>
    </template>
  </AppModal>
</template>

<script setup>
import { computed, watch } from 'vue';
import { GAME_TYPES } from '../constants/gameTypes';
import { useUnitAllocation } from '../composables/useUnitAllocation';
import AppModal from './AppModal.vue';
import GameIndex from './GameIndex.vue';
import UnitPlayerList from './UnitPlayerList.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  game: {
    type: Object,
    default: null
  },
  players: {
    type: Array,
    required: true
  },
  isDouble: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['confirm', 'close']);

const title = computed(() => props.game?.displayName ?? '');

const maxUnits = computed(() => props.game?.maxUnits?.(props.players.length) ?? 0);

const allocation = useUnitAllocation(() => props.players, maxUnits);

watch([() => props.players, () => props.isOpen], () => allocation.reset(), { deep: true });

const unitName = computed(() => {
  switch (props.game?.id) {
    case GAME_TYPES.DIAMONDS:
      return 'diamonds';
    case GAME_TYPES.QUEENS:
      return 'queens';
    default:
      return 'hands';
  }
});

const instructionText = computed(() => {
  const verb = unitName.value === 'hands' ? 'win' : 'take';
  const doubled = props.isDouble ? ' Points are doubled.' : '';
  return `How many ${unitName.value} did each player ${verb}?${doubled}`;
});

const statusText = computed(() => {
  const remaining = allocation.remaining.value;
  if (remaining === 0) return `All ${maxUnits.value} ${unitName.value} assigned`;
  return `${remaining} of ${maxUnits.value} ${unitName.value} left to assign`;
});

const baseScore = (playerId) =>
  (allocation.counts.value[playerId] ?? 0) * (props.game?.points ?? 0);

const pointsFor = (playerId) => baseScore(playerId) * (props.isDouble ? 2 : 1);

const handleConfirm = () => {
  if (!allocation.isComplete.value) return;
  emit(
    'confirm',
    props.players.map((player) => ({ playerId: player.id, score: baseScore(player.id) }))
  );
};
</script>

<style scoped>
.status.is-done {
  color: var(--ink);
  font-weight: 600;
}
</style>
